import React, { useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Dashboard, TabGroup } from "api/model/dashboard/Dashboard";
import { DashboardTabModel } from "api/model/dashboard/DashboardTab";
import { T } from "services/lang/LanguageService";
import DraggableItem, { ReorderType } from "./tab/reorder/DraggableItem";
import DraggableList from "./DraggableList";

interface ReorderTabsAndGroupsProps {
  isVisible: boolean;
  setIsVisible: (isVisible: boolean) => void;
  dashboard: Dashboard;
  setDashboard: (groups: Dashboard) => void;
}

interface ReorderItem {
  uuid: string;
  title: string;
  isTabPlaceholder?: boolean;
}

const ReorderTabsAndGroups = ({
  isVisible,
  setIsVisible,
  dashboard,
  setDashboard,
}: ReorderTabsAndGroupsProps) => {
  const [isReorderDashboardGroupVisible, setIsReorderDashboardGroupVisible] = useState(false);
  const [groupTabOrderMap, setGroupTabOrderMap] = useState<Map<string, DashboardTabModel[]>>(
    new Map()
  );

  const [selectedGroup, setSelectedGroup] = useState<string>("");
  const tabGroups = dashboard?.tabGroups ?? [];
  const tabs = dashboard?.dashboardTabsJs ?? [];

  const groupMap = new Map(tabGroups.map(group => [group.uuid, group]));

  const tabCountByGroupId = new Map<string, number>();
  tabs.forEach(tab => {
    if (tab.tabGroupId) {
      tabCountByGroupId.set(tab.tabGroupId, (tabCountByGroupId.get(tab.tabGroupId) ?? 0) + 1);
    }
  });

  const orderedItems: ReorderItem[] = [];

  tabGroups.forEach(group => {
    const groupTabs = tabs.filter(tab => tab.tabGroupId === group.uuid);
    const isSingleTabPlaceholder = group.isTabPlaceholder && groupTabs.length === 1;

    if (isSingleTabPlaceholder) {
      const tab = groupTabs[0];
      orderedItems.push({
        uuid: tab.uuid,
        title: tab.title,
        isTabPlaceholder: true,
      });
    } else if (groupTabs.length > 0) {
      orderedItems.push({
        uuid: group.uuid,
        title: group.title,
        isTabPlaceholder: false,
      });
    }
  });

  const ungroupedTabs = tabs.filter(tab => {
    const group = tab.tabGroupId ? groupMap.get(tab.tabGroupId) : undefined;
    return !group;
  });

  orderedItems.unshift(
    ...ungroupedTabs.map(tab => ({
      uuid: tab.uuid,
      title: tab.title,
      isTabPlaceholder: true,
    }))
  );

  const [items, setItems] = useState<ReorderItem[]>(orderedItems);

  const handleReorderDashboardGroup = (groupId: string) => {
    setSelectedGroup(groupId);
    setIsReorderDashboardGroupVisible(true);

    setGroupTabOrderMap(prev => {
      const newMap = new Map(prev);
      if (!newMap.has(groupId)) {
        const originalTabs = tabs.filter(tab => tab.tabGroupId === groupId);
        newMap.set(groupId, originalTabs);
      }
      return newMap;
    });
  };

  const onDragEnd = (result: any) => {
    if (!result.destination) return;
    const newItems = Array.from(items);
    const [moved] = newItems.splice(result.source.index, 1);
    newItems.splice(result.destination.index, 0, moved);
    setItems(newItems);
  };

  const onDragEndDashboardGroup = (result: any) => {
    if (!result.destination || !selectedGroup) return;

    const currentTabs = groupTabOrderMap.get(selectedGroup);
    if (!currentTabs) return;

    const reordered = [...currentTabs];
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);

    setGroupTabOrderMap(prev => {
      const newMap = new Map(prev);
      newMap.set(selectedGroup, reordered);
      return newMap;
    });
  };

  const handleSave = () => {
    const reorderedGroups: TabGroup[] = [];
    let updatedTabs = [...dashboard.dashboardTabsJs];

    groupTabOrderMap.forEach((reorderedTabs, groupId) => {
      updatedTabs = updatedTabs.filter(tab => tab.tabGroupId !== groupId);
      updatedTabs.push(...reorderedTabs.map(tab => ({ ...tab, tabGroupId: groupId })));
    });

    items.forEach(item => {
      reorderedGroups.push({
        uuid: item.uuid,
        title: item.title,
        isTabPlaceholder: item.isTabPlaceholder,
      });

      if (item.isTabPlaceholder) {
        const matchingTab = updatedTabs.find(tab => tab.uuid === item.uuid);
        if (matchingTab) {
          matchingTab.tabGroupId = item.uuid;
        }
      }
    });

    setDashboard({
      ...dashboard,
      tabGroups: reorderedGroups,
      dashboardTabsJs: updatedTabs,
    });
    setIsVisible(false);
  };

  const footerTemplate = (isDashboardGroup?: boolean) => (
    <div className="flex justify-content-end gap-2 mt-3">
      <Button
        label={T("action.cancel")}
        icon="pi pi-times"
        className="component-editor-button icon-red"
        onClick={() =>
          isDashboardGroup ? setIsReorderDashboardGroupVisible(false) : setIsVisible(false)
        }
      />
      {!isDashboardGroup && (
        <Button
          label={T("action.save")}
          icon="pi pi-check"
          className="component-editor-button icon-green"
          onClick={handleSave}
          autoFocus
        />
      )}
    </div>
  );

  return (
    <>
      <Dialog
        footer={footerTemplate()}
        header={T("label.reorder-dropdown-menu-items")}
        visible={isVisible}
        onHide={() => setIsVisible(false)}
        className="w-30rem"
      >
        <DraggableList
          items={items}
          onDragEnd={onDragEnd}
          droppableId="reorder-list-main"
          getKey={item => item.uuid}
          renderItem={(item, index, prov) => (
            <DraggableItem
              item={item}
              provided={prov}
              type={ReorderType.DD_MENU_ITEMS}
              className={item.isTabPlaceholder ? "" : "font-bold"}
              isGroup={!item.isTabPlaceholder}
              reorderDashboardGroup={() => handleReorderDashboardGroup(item.uuid)}
            />
          )}
        />
      </Dialog>

      {isReorderDashboardGroupVisible && (
        <Dialog
          footer={() => footerTemplate(true)}
          header={T("label.reorder-dashboard-group", { group: groupMap.get(selectedGroup)?.title })}
          visible={isVisible}
          onHide={() => setIsVisible(false)}
          className="w-30rem"
        >
          <DraggableList
            items={groupTabOrderMap.get(selectedGroup) ?? []}
            onDragEnd={onDragEndDashboardGroup}
            droppableId="reorder-list-group"
            getKey={item => item.uuid}
            renderItem={(item, index, prov) => (
              <DraggableItem item={item} provided={prov} type={ReorderType.DD_MENU_ITEMS} />
            )}
          />
        </Dialog>
      )}
    </>
  );
};

export default ReorderTabsAndGroups;
